import type { EChartsOption, EChartsType } from 'echarts';
import * as echarts from 'echarts';
import { useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type PieChartDataItem = {
  name: string;
  value: number;
  color?: string;
};

type EChartsPieChartProps = {
  data: PieChartDataItem[];
  containerClassName?: string;
  formatLegendValue?: (value: number) => string;
};

const LABEL_H = 13;
const LABEL_GAP = 5;
const LABEL_FONT_SIZE = 12;
const LABEL_TEXT_PAD = 3;
const MAX_LABEL_TEXT_WIDTH = 54;
const CANVAS_LABEL_PAD = 10;

// Below this rendered chart width, stack the legend below a centered pie.
const NARROW_BREAKPOINT = 500;
const LEGEND_ICON_AND_GAP = 20;
const MIN_LABEL_LEGEND_GAP = 12;
const DEFAULT_LABEL_LEGEND_GAP = 28;
const LEGEND_RIGHT_PAD = 8;
const LEGEND_FONT_SIZE = 13;
const LEGEND_CHAR_WIDTH = 6.5;
const LEGEND_LINE_HEIGHT = 18;

interface SliceInfo {
  pct: number;
  color: string;
  ex: number;
  ey: number; // point on pie edge
  ax: number;
  ay: number; // end of radial arm (elbow)
  onRight: boolean;
  naturalY: number;
  labelY: number;
}

// Read the actual rendered pie geometry from ECharts model —
// never compute it manually, it won't match what ECharts drew.
function getPieGeometry(chart: EChartsType): { cx: number; cy: number; r: number; W: number; H: number } {
  const model = (chart as any).getModel().getSeriesByIndex(0);
  const center = model.get('center') as [string | number, string | number];
  const radius = model.get('radius') as [string | number, string | number];
  const W = chart.getWidth();
  const H = chart.getHeight();

  const parse = (v: string | number, total: number) =>
    typeof v === 'string' && v.endsWith('%') ? (parseFloat(v) / 100) * total : parseFloat(v as string);

  return {
    cx: parse(center[0], W),
    cy: parse(center[1], H),
    r: parse(radius[1], Math.min(W, H) / 2),
    W,
    H,
  };
}

function computeSlices(data: PieChartDataItem[], cx: number, cy: number, r: number): SliceInfo[] {
  const total = data.reduce((s, d) => s + d.value, 0);
  const LINE1 = r * 0.25; // radial arm length, proportional to pie radius
  const _LINE2 = r * 0.2; // horizontal arm length

  let startAngle = -Math.PI / 2;

  return data.map((item) => {
    const pct = item.value / total;
    const sweep = pct * 2 * Math.PI;
    const mid = startAngle + sweep / 2;
    startAngle += sweep;

    const cos = Math.cos(mid);
    const sin = Math.sin(mid);

    const ex = cx + r * cos;
    const ey = cy + r * sin;
    const ax = cx + (r + LINE1) * cos;
    const ay = cy + (r + LINE1) * sin;
    const onRight = cos >= 0;

    return {
      pct: pct * 100,
      color: item.color ?? '#888888',
      ex,
      ey,
      ax,
      ay,
      onRight,
      naturalY: ay,
      labelY: ay,
    };
  });
}

function resolveOverlaps(slices: SliceInfo[]): void {
  // Process left and right sides independently, top to bottom
  (['right', 'left'] as const).forEach((side) => {
    const group = slices.filter((s) => (side === 'right') === s.onRight).sort((a, b) => a.naturalY - b.naturalY);

    for (let i = 1; i < group.length; i++) {
      const minY = group[i - 1].labelY + LABEL_H + LABEL_GAP;
      if (group[i].labelY < minY) group[i].labelY = minY;
    }
  });
}

/** Keep de-overlapped labels inside the canvas so percentage text is never clipped. */
function clampSlicesToCanvas(slices: SliceInfo[], canvasHeight: number): void {
  const minY = CANVAS_LABEL_PAD + LABEL_H / 2;
  const maxY = canvasHeight - CANVAS_LABEL_PAD - LABEL_H / 2;

  (['right', 'left'] as const).forEach((side) => {
    const group = slices.filter((s) => (side === 'right') === s.onRight).sort((a, b) => a.labelY - b.labelY);

    if (group.length === 0) return;

    // Pull top labels into view first, then push stacked ones back apart.
    if (group[0].labelY < minY) group[0].labelY = minY;
    for (let i = 1; i < group.length; i++) {
      const minNext = group[i - 1].labelY + LABEL_H + LABEL_GAP;
      if (group[i].labelY < minNext) group[i].labelY = minNext;
    }

    // If the stack overflows the bottom, shift the whole side up.
    const overflow = group[group.length - 1].labelY - maxY;
    if (overflow > 0) {
      for (const slice of group) slice.labelY -= overflow;
      if (group[0].labelY < minY) {
        // Not enough vertical room — compress into the available band.
        const usable = Math.max(maxY - minY, LABEL_H);
        const step = group.length > 1 ? usable / (group.length - 1) : 0;
        group.forEach((slice, index) => {
          slice.labelY = minY + step * index;
        });
      }
    }
  });
}

function paintLabels(canvas: HTMLCanvasElement, slices: SliceInfo[], r: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const LINE2 = r * 0.2;
  const minLabelX = CANVAS_LABEL_PAD + MAX_LABEL_TEXT_WIDTH;
  const maxLabelX = canvas.width - CANVAS_LABEL_PAD - MAX_LABEL_TEXT_WIDTH;

  slices.forEach((s) => {
    let labelX = s.onRight ? s.ax + LINE2 : s.ax - LINE2;
    // Keep horizontal text inside the canvas.
    labelX = s.onRight ? Math.min(Math.max(labelX, s.ax), maxLabelX) : Math.max(Math.min(labelX, s.ax), minLabelX);

    const text = `${s.pct.toFixed(2)}%`;
    const fontSize = LABEL_FONT_SIZE;

    ctx.strokeStyle = s.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(s.ex, s.ey);
    ctx.lineTo(s.ax, s.labelY);
    ctx.lineTo(labelX, s.labelY);
    ctx.stroke();

    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.fillStyle = s.color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = s.onRight ? 'left' : 'right';
    ctx.fillText(text, s.onRight ? labelX + LABEL_TEXT_PAD : labelX - LABEL_TEXT_PAD, s.labelY);
  });
}

function getLegendLabelTexts(data: PieChartDataItem[], formatLegendValue?: (value: number) => string): string[] {
  return data.map((item) => {
    const formattedValue = formatLegendValue ? formatLegendValue(item.value) : String(item.value);
    return `${item.name}: ${formattedValue}`;
  });
}

function computeLabelBounds(
  data: PieChartDataItem[],
  cx: number,
  cy: number,
  r: number
): { left: number; right: number; top: number; bottom: number } {
  const slices = computeSlices(data, cx, cy, r);
  resolveOverlaps(slices);
  const line2 = r * 0.2;

  let left = cx - r;
  let right = cx + r;
  let top = cy - r;
  let bottom = cy + r;

  slices.forEach((slice) => {
    const labelX = slice.onRight ? slice.ax + line2 : slice.ax - line2;
    if (slice.onRight) {
      right = Math.max(right, labelX + LABEL_TEXT_PAD + MAX_LABEL_TEXT_WIDTH);
    } else {
      left = Math.min(left, labelX - LABEL_TEXT_PAD - MAX_LABEL_TEXT_WIDTH);
    }
    top = Math.min(top, slice.labelY - LABEL_H / 2);
    bottom = Math.max(bottom, slice.labelY + LABEL_H / 2);
  });

  return { left, right, top, bottom };
}

function estimateLegendWidth(labelTexts: string[], maxWidth: number): number {
  const longestChars = Math.max(...labelTexts.map((text) => text.length), 0);
  const textWidth = longestChars * LEGEND_CHAR_WIDTH;
  return Math.max(120, Math.min(maxWidth, textWidth));
}

function estimateLegendHeight(labelTexts: string[], legendTextWidth: number): number {
  return labelTexts.reduce((total, text) => {
    const charsPerLine = Math.max(1, Math.floor(legendTextWidth / LEGEND_CHAR_WIDTH));
    const lines = Math.max(1, Math.ceil(text.length / charsPerLine));
    return total + lines * LEGEND_LINE_HEIGHT + 12;
  }, 0);
}

function computeGapFromLegendLength(longestChars: number): number {
  const shortThreshold = 22;
  const longThreshold = 42;

  if (longestChars <= shortThreshold) return DEFAULT_LABEL_LEGEND_GAP;
  if (longestChars >= longThreshold) return MIN_LABEL_LEGEND_GAP;

  const progress = (longestChars - shortThreshold) / (longThreshold - shortThreshold);
  return DEFAULT_LABEL_LEGEND_GAP - progress * (DEFAULT_LABEL_LEGEND_GAP - MIN_LABEL_LEGEND_GAP);
}

interface ChartLayout {
  center: [string, string];
  radius: [string, string];
  legend: {
    orient: 'vertical';
    left: string;
    top?: 'middle';
    bottom?: number;
    align: 'left';
    width: number;
    textWidth: number;
  };
}

function computeWideLayout(
  data: PieChartDataItem[],
  chartWidth: number,
  chartHeight: number,
  formatLegendValue?: (value: number) => string
): ChartLayout {
  const labelTexts = getLegendLabelTexts(data, formatLegendValue);
  const longestChars = Math.max(...labelTexts.map((text) => text.length), 0);
  const gap = computeGapFromLegendLength(longestChars);

  const radiusBase = Math.min(chartWidth, chartHeight) / 2;
  const preferredLegendWidth = estimateLegendWidth(labelTexts, chartWidth * 0.4);
  const minLegendWidth = Math.min(preferredLegendWidth, Math.max(100, chartWidth * 0.22));

  let centerX = chartWidth * 0.32;
  let centerY = chartHeight * 0.5;
  let r = radiusBase * 0.42;

  let labelBounds = computeLabelBounds(data, centerX, centerY, r);
  let legendLeft = Math.max(labelBounds.right + gap, chartWidth - preferredLegendWidth - LEGEND_RIGHT_PAD);
  let legendWidth = chartWidth - legendLeft - LEGEND_RIGHT_PAD;

  for (let attempt = 0; attempt < 24; attempt += 1) {
    labelBounds = computeLabelBounds(data, centerX, centerY, r);
    legendLeft = Math.max(labelBounds.right + gap, chartWidth - preferredLegendWidth - LEGEND_RIGHT_PAD);
    legendWidth = chartWidth - legendLeft - LEGEND_RIGHT_PAD;

    const leftOverflow = CANVAS_LABEL_PAD - labelBounds.left;
    const rightOverflow = labelBounds.right + gap + minLegendWidth + LEGEND_RIGHT_PAD - chartWidth;
    const topOverflow = CANVAS_LABEL_PAD - labelBounds.top;
    const bottomOverflow = labelBounds.bottom + CANVAS_LABEL_PAD - chartHeight;
    const fits = leftOverflow <= 0 && rightOverflow <= 0 && topOverflow <= 0 && bottomOverflow <= 0 && legendWidth >= minLegendWidth;

    if (fits) break;

    if (leftOverflow > 0) centerX += Math.min(leftOverflow + 4, chartWidth * 0.04);
    if (rightOverflow > 0) centerX -= Math.min(rightOverflow * 0.35, chartWidth * 0.03);
    if (topOverflow > 0) centerY += Math.min(topOverflow + 4, chartHeight * 0.03);
    if (bottomOverflow > 0) centerY -= Math.min(bottomOverflow + 4, chartHeight * 0.03);

    // Keep the center in a safe band while shrinking the pie.
    centerX = Math.min(Math.max(centerX, chartWidth * 0.22), chartWidth * 0.45);
    centerY = Math.min(Math.max(centerY, chartHeight * 0.35), chartHeight * 0.62);
    r = Math.max(radiusBase * 0.24, r * 0.94);
  }

  labelBounds = computeLabelBounds(data, centerX, centerY, r);
  legendLeft = Math.max(labelBounds.right + gap, Math.min(chartWidth * 0.62, chartWidth - minLegendWidth - LEGEND_RIGHT_PAD));
  legendWidth = Math.max(minLegendWidth, chartWidth - legendLeft - LEGEND_RIGHT_PAD);

  return {
    center: [`${(centerX / chartWidth) * 100}%`, `${(centerY / chartHeight) * 100}%`] as [string, string],
    radius: ['0%', `${(r / radiusBase) * 100}%`] as [string, string],
    legend: {
      orient: 'vertical' as const,
      left: `${(legendLeft / chartWidth) * 100}%`,
      top: 'middle' as const,
      align: 'left' as const,
      width: legendWidth,
      textWidth: Math.max(80, legendWidth - LEGEND_ICON_AND_GAP),
    },
  };
}

function computeNarrowLayout(
  data: PieChartDataItem[],
  chartWidth: number,
  chartHeight: number,
  formatLegendValue?: (value: number) => string
): ChartLayout {
  const labelTexts = getLegendLabelTexts(data, formatLegendValue);
  const longestChars = Math.max(...labelTexts.map((text) => text.length), 0);
  const gap = computeGapFromLegendLength(longestChars);

  const legendWidth = Math.max(160, chartWidth - 32);
  const legendTextWidth = legendWidth - LEGEND_ICON_AND_GAP;
  const legendHeight = estimateLegendHeight(labelTexts, legendTextWidth);

  const centerX = chartWidth * 0.5;
  const radiusBase = Math.min(chartWidth, chartHeight) / 2;
  let r = radiusBase * 0.42;
  let centerY = chartHeight * 0.38;
  const bottomPad = 8;
  const maxLabelBottom = chartHeight - bottomPad - legendHeight - gap;

  for (let attempt = 0; attempt < 16; attempt += 1) {
    const labelBounds = computeLabelBounds(data, centerX, centerY, r);
    const leftOverflow = CANVAS_LABEL_PAD - labelBounds.left;
    const rightOverflow = labelBounds.right + CANVAS_LABEL_PAD - chartWidth;
    const topOverflow = CANVAS_LABEL_PAD - labelBounds.top;
    const bottomOverflow = labelBounds.bottom - maxLabelBottom;

    if (leftOverflow <= 0 && rightOverflow <= 0 && topOverflow <= 0 && bottomOverflow <= 0) break;

    if (topOverflow > 0) centerY += Math.min(topOverflow + 4, chartHeight * 0.03);
    if (bottomOverflow > 0) centerY -= Math.min(bottomOverflow + 4, chartHeight * 0.03);
    centerY = Math.min(Math.max(centerY, chartHeight * 0.28), chartHeight * 0.45);
    r = Math.max(radiusBase * 0.24, r * 0.94);
  }

  return {
    center: [`${(centerX / chartWidth) * 100}%`, `${(centerY / chartHeight) * 100}%`] as [string, string],
    radius: ['0%', `${(r / radiusBase) * 100}%`] as [string, string],
    legend: {
      orient: 'vertical' as const,
      left: 'center' as const,
      bottom: bottomPad,
      align: 'left' as const,
      width: legendWidth,
      textWidth: legendTextWidth,
    },
  };
}

function getLegendLayout(
  data: PieChartDataItem[],
  narrow: boolean,
  chartWidth: number,
  chartHeight: number,
  formatLegendValue?: (value: number) => string
): ChartLayout {
  return narrow
    ? computeNarrowLayout(data, chartWidth, chartHeight, formatLegendValue)
    : computeWideLayout(data, chartWidth, chartHeight, formatLegendValue);
}

function buildChartOption(
  data: PieChartDataItem[],
  narrow: boolean,
  chartWidth: number,
  chartHeight: number,
  formatLegendValue?: (value: number) => string
): EChartsOption {
  const layout = getLegendLayout(data, narrow, chartWidth, chartHeight, formatLegendValue);
  const { center, radius, legend: legendLayout } = layout;

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        if (!params || typeof params !== 'object' || !('name' in params)) return '';
        const { name, value, percent } = params as {
          name: string;
          value: number;
          percent: number;
        };
        const formattedValue = formatLegendValue ? formatLegendValue(Number(value)) : String(value);
        return `${name}: ${formattedValue} (${percent}%)`;
      },
    },
    legend: {
      show: true,
      type: 'plain',
      orient: legendLayout.orient,
      left: legendLayout.left,
      ...(legendLayout.top != null ? { top: legendLayout.top } : {}),
      ...(legendLayout.bottom != null ? { bottom: legendLayout.bottom } : {}),
      align: legendLayout.align,
      width: legendLayout.width,
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 12,
      icon: 'circle',
      textStyle: {
        color: '#111827',
        fontSize: LEGEND_FONT_SIZE,
        fontWeight: 500,
        lineHeight: LEGEND_LINE_HEIGHT,
        width: legendLayout.textWidth,
        overflow: 'break',
      },
      formatter: (name: string) => {
        const item = data.find((entry) => entry.name === name);
        if (!item) return name;
        const formattedValue = formatLegendValue ? formatLegendValue(item.value) : String(item.value);
        return `${name}: ${formattedValue}`;
      },
    },
    series: [
      {
        name: 'Chart',
        type: 'pie',
        center,
        radius,
        // All labels drawn manually on canvas overlay so ECharts labels disabled
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderColor: '#fff', borderWidth: 1.5 },
        emphasis: { scale: false },
        data: data.map((item) => ({
          name: item.name,
          value: item.value,
          itemStyle: item.color ? { color: item.color } : undefined,
        })),
      },
    ],
  };
}

const PieChartECharts = ({ data, containerClassName, formatLegendValue }: EChartsPieChartProps) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const instanceRef = useRef<EChartsType | null>(null);

  const redrawLabels = useCallback(() => {
    const chart = instanceRef.current;
    const canvas = canvasRef.current;
    if (!chart || !canvas || data.length === 0) return;

    const { cx, cy, r, W, H } = getPieGeometry(chart);
    canvas.width = W;
    canvas.height = H;

    const slices = computeSlices(data, cx, cy, r);
    resolveOverlaps(slices);
    clampSlicesToCanvas(slices, H);
    paintLabels(canvas, slices, r);
  }, [data]);

  // Apply the option using a layout chosen from the current rendered width.
  const applyOption = useCallback(() => {
    const chart = instanceRef.current;
    if (!chart || data.length === 0) return;
    const narrow = chart.getWidth() < NARROW_BREAKPOINT;
    chart.setOption(buildChartOption(data, narrow, chart.getWidth(), chart.getHeight(), formatLegendValue), true);
  }, [data, formatLegendValue]);

  // Keep the latest callbacks in refs so the mount effect can stay stable.
  const applyOptionRef = useRef(applyOption);
  const redrawRef = useRef(redrawLabels);
  useEffect(() => {
    applyOptionRef.current = applyOption;
    redrawRef.current = redrawLabels;
  }, [applyOption, redrawLabels]);

  // Mount chart once
  useEffect(() => {
    const wrapEl = wrapRef.current;
    const chartEl = chartRef.current;
    if (!wrapEl || !chartEl) return;
    const chart = echarts.init(chartEl);
    instanceRef.current = chart;

    const onFinished = () => redrawRef.current();
    chart.on('finished', onFinished);

    const ro = new ResizeObserver(() => {
      chart.resize();
      applyOptionRef.current();
    });
    // Observe the sized wrapper (not the absolute chart layer) so width changes always reflow.
    ro.observe(wrapEl);

    return () => {
      chart.off('finished', onFinished);
      ro.disconnect();
      chart.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    applyOption();
  }, [applyOption]);

  return (
    <div ref={wrapRef} className={twMerge('relative min-h-[400px] w-full min-w-0', containerClassName)}>
      <div ref={chartRef} className='absolute inset-0' />
      <canvas ref={canvasRef} className='absolute inset-0 pointer-events-none' />
    </div>
  );
};

export default PieChartECharts;
